// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { NotificationService } from '@way/theme-host/services/notification.service';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  ChampsPageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { ChampsPageFooterComponent } from '../shared/footer/footer.component';
import { SharedBookAppointmentModalComponent } from '@way/website-themes/themes/shared/book-appointment-modal/book-appointment-modal.component';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '@way/theme-host/shared/components/success-modal/success-modal.component';
import { LocationService } from '@way/theme-host/services/location.service';
import { AppointmentService } from '@way/theme-host/services/appointment.service';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'express-details'
    | 'packages'
    | 'coupons'
    | 'fleets';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-champs-express-auto-details-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    ChampsPageHeaderComponent,
    ChampsPageFooterComponent,
    SharedBookAppointmentModalComponent,
    SuccessModalComponent,
  ],
  templateUrl: './express-auto-details-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-auto-details-preview.component.scss'],
})
export class ChampsExpressAutoDetailsPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {}; // Section visibility control
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;

  // Modal state
  showBookAppointmentModal = false;
  selectedPackageForBooking: Package | null = null;

  // Location data
  locations: any[] = [];
  isLoadingLocations = false;
  locationsError = '';

  // Appointment submission state
  isSubmittingAppointment = false;
  appointmentSuccess = false;
  appointmentError = '';
  showSuccessModal = false;
  successModalData: SuccessModalData = {
    title: 'Appointment Booked Successfully!',
    description: 'Your appointment has been confirmed. We look forward to serving you.',
    buttonText: 'Okay',
    showTimeInfo: false,
    timeInfo: '',
  };

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  servicesSectionData: any = null;
  footerData: any = null;

  // Loading and animation state
  isPageLoaded = false;
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<Element>();

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private messageService: NotificationService,
    private locationService: LocationService,
    private appointmentService: AppointmentService
  ) {}

  /**
   * Initialize the preview component: load page content and packages, set up page loading state and trigger entry/scroll animations after load.
   * @example
   * gOnInit()
   * // void
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();

    // Initialize page loading state
    this.isPageLoaded = false;

    // Simulate page load animation
    setTimeout(() => {
      this.isPageLoaded = true;
      this.cdr.detectChanges();
      // Initialize scroll animations after page loads
      setTimeout(() => this.initScrollAnimations(), 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.animatedElements.clear();
  }

  /**
   * Initialize scroll animations using Intersection Observer
   */
  private initScrollAnimations(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return; // Fallback for browsers without IntersectionObserver
    }

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Create intersection observer for scroll animations with optimized settings
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                entry.target.classList.add('animate-in');
                this.animatedElements.add(entry.target);
                // Unobserve after animation to improve performance
                this.intersectionObserver?.unobserve(entry.target);
              }
            });
          });
        },
        {
          threshold: [0.1, 0.2], // Multiple thresholds for smoother detection
          rootMargin: '0px 0px -80px 0px', // Reduced margin for better scroll performance
        }
      );

      // Observe all animatable elements with delay to avoid blocking initial render
      setTimeout(() => {
        const elements = document.querySelectorAll(
          '.express-auto-details-preview .website-element'
        );
        elements.forEach(el => {
          // Check if element is already in viewport
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;

          el.classList.add('scroll-animate');

          // If element is already visible, animate it immediately
          if (isInViewport) {
            requestAnimationFrame(() => {
              el.classList.add('animate-in');
              this.animatedElements.add(el);
            });
          } else {
            // Only observe if not already in viewport
            this.intersectionObserver?.observe(el);
          }
        });
      }, 300);
    });
  }

  /**
   * Responds to input property changes: reloads page content, updates header navigation, and refreshes section data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, elements: { firstChange: false }, pages: {}, currentPageType: {} })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Object containing SimpleChange entries for each changed input property.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.servicesSectionData = sections.services_section || {};
    }
  }

  /**
   * Load and parse the Express Auto Details page data into the component's section properties.
   * @example
   * loadPageContent()
   * void
   * @returns {void} No return value; updates component state (headerData, pageTitleData, servicesSectionData, footerData) and header navigation.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('✨ Loading Express Auto Details page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.servicesSectionData = sections.services_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Express Auto Details sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      services: this.servicesSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update header navigation from pages and ensure a default CTA button exists on headerData.
   * @example
   * this.updateHeaderNavigation()
   * undefined
   * @returns {{void}} Does not return a value; updates this.headerData in place.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Auto Details:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Load detailing packages from the PackageService and update component state (this.packages, this.loading, this.error).
   * @example
   * loadPackages()
   * undefined
   * @returns {{void}} Does not return a value; updates component state with packages on success or sets an error on failure.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getDetailingPackages().subscribe({
      next: response => {
        this.logger.debug('✅ Detailing packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.logger.debug('Packages count:', this.packages.length);
        } else {
          this.error = 'No detailing packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('❌ Error loading detailing packages:', err);
        this.error = 'Failed to load detailing packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine CSS object-fit value for a package's image based on its scaletype setting from servicesSectionData.packages.
   * @example
   * etPackageImageFit({ id: 'pkg1' })
   * 'cover'
   * @param {{Package}} {{pkg}} - Package object to look up and map scaletype to a CSS object-fit value.
   * @returns {{string}} Return the CSS object-fit value (e.g., 'cover', 'contain', 'fill', 'scale-down').
   **/
  getPackageImageFit(pkg: Package): string {
    // Check services_section.packages array for this package's scaletype
    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Get display price
  getListPrice(pkg: Package): string {
    return pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    return pkg.single_strikeoff_price || pkg.single_list_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Get the header logo object, preferring the header element's logo data, then pageData branding, and falling back to defaults.
   * @example
   * etHeaderLogo()
   * { image: '', text: 'Logo', link: '/', alt: 'Logo' }
   * @param {void} none - No arguments.
   * @returns {{image: string, text: string, link: string, alt: string}} The resolved logo object with image, text, link and alt properties.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    // Priority 1: Check header element content (most up-to-date)
    if (headerContent?.logo) {
      // If logo has image/url, return it
      if (headerContent.logo.image || headerContent.logo.url) {
        return {
          image: headerContent.logo.image || headerContent.logo.url || '',
          text: headerContent.logo.text || 'Logo',
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
      // If logo has text but no image, return text logo
      if (headerContent.logo.text) {
        return {
          image: '',
          text: headerContent.logo.text,
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
    }
    // Priority 2: Check branding data from pageData
    if (this.pageData?.json_data?.website?.branding?.logo) {
      const brandingLogo = this.pageData.json_data.website.branding.logo;
      if (brandingLogo.url || brandingLogo.image) {
        return {
          image: brandingLogo.url || brandingLogo.image || '',
          text: brandingLogo.text || 'Logo',
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
      if (brandingLogo.text) {
        return {
          image: '',
          text: brandingLogo.text,
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
    }
    return { text: 'Logo', link: '/', image: '', alt: 'Logo' };
  }

  /**
   * Build header navigation items from header content or fallback pages, normalizing links and ensuring pageType is set.
   * @example
   * etHeaderNavigation()
   * [
   *   { label: 'Home', link: '#home', pageType: 'home', active: true },
   *   { label: 'Express Washes', link: '#express-wash-packages', pageType: 'express-wash-packages' },
   *   { label: 'Fleet', link: '#fleet-management', pageType: 'fleet-management' }
   * ]
   * @returns {NavigationItem[]} Returns an array of navigation items (label, link, pageType, optional active).
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Returns the header CTA button configuration from header content or defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @param {void} none - No parameters.
   * @returns {CTAButton | null} CTA button object from header content or a default/fallback object.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Auto Details – Shine in No Time!';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return "Whether it's a quick clean or a full headlamp refresh, our Express Detail Packages give your ride that 'like-new' look without the wait.";
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    // Handle both editor and preview formats
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get hero section element for selection
   */
  getPageHeaderSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'express-details' ||
        el.id === 'express-details' ||
        (el as any).originalKey === 'page-title'
    );
  }

  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const pageHeaderElement = this.getPageHeaderSectionElement();
    const pageHeaderContent = pageHeaderElement?.content as any;
    if (pageHeaderContent?.backgroundColor) {
      return pageHeaderContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check services section background color (also saved there)
    if (this.servicesSectionData?.backgroundColor) {
      return this.servicesSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    // Priority 6: Check services section in pageData
    const servicesSection = sections.services_section || {};
    if (servicesSection.backgroundColor) {
      return servicesSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get services section background color (same as page header section)
   */
  getServicesSectionBackgroundColor(): string | null {
    // Use the same background color as the page header section
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    // Don't handle section click if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer') ||
      target.closest('.page-header-header-wrapper')
    ) {
      return; // Let navigation handle the click
    }

    event.stopPropagation();
    const heroElement: WebsiteElement = {
      id: 'express-details',
      type: 'express-details',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        backgroundColor:
          this.getPageHeaderSectionBackgroundColor() || this.pageTitleData?.backgroundColor,
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('✨ Hero section clicked, emitting element:', heroElement);
    this.elementSelect.emit(heroElement);
  }

  /**
   * Get an array of footer quick links from the footer content or derive them from visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: "Contact", link: "#contact" }, { label: "About Us", link: "#about-us" }]
   * @returns {{Array<{ label: string; link: string }>} } Array of objects each containing a label and link.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Return normalized social links extracted from the component's footer content.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'facebook', url: 'https://facebook.com/example', icon: 'facebook' }, { platform: 'twitter', url: 'https://twitter.com/example', icon: 'twitter' }]
   * @returns {Array<{ platform: string; url: string; icon: string }>} An array of social link objects each containing platform, url, and icon.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    // Handle different data structures:
    // 1. socialLinks as array (from website builder)
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 2. socialLinks as object with platforms array (from website preview)
    if (
      footerContent?.socialLinks?.platforms &&
      Array.isArray(footerContent.socialLinks.platforms)
    ) {
      return footerContent.socialLinks.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 3. Check for social_links.platforms (alternative structure)
    if (
      footerContent?.social_links?.platforms &&
      Array.isArray(footerContent.social_links.platforms)
    ) {
      return footerContent.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 4. Check for social_links as array
    if (footerContent?.social_links && Array.isArray(footerContent.social_links)) {
      return footerContent.social_links.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.socialLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.quickLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const copyright = footerContent?.copyright;
    if (typeof copyright === 'string') {
      return copyright;
    } else if (copyright && typeof copyright === 'object' && copyright.text) {
      return copyright.text;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo (text and image) from the footer content, falling back to the header logo or a default.
   * @example
   * this.etFooterLogo()
   * { text: 'way', image: '' }
   * @returns {{text?: string, image?: string}|undefined} The logo object containing optional text and image properties, or undefined if no footer/header logo is available.
   */
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Handle header click: prevent propagation when clicking navigation, CTA, hamburger or mobile menu elements; otherwise locate the header element and trigger an element click handler.
   * @example
   * nHeaderClick(event)
   * undefined
   * @param {{Event}} {{event}} - The click event originating from the header area.
   * @returns {{void}} No return value.
   **/
  onHeaderClick(event: Event): void {
    // Don't propagate if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer')
    ) {
      event.stopPropagation();
      return;
    }

    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  /**
   * Handle click on the footer: stop event propagation, ensure the footer element has type and id set to 'footer', and delegate the click to element handler.
   * @example
   * nFooterClick(event)
   * undefined
   * @param {{Event}} {{event}} - The DOM event triggered by clicking the footer; propagation will be stopped.
   * @returns {{void}} No return value.
   **/
  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      // Ensure footer element has correct type and id
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  // Modal methods
  onBookAppointment(pkg: Package): void {
    this.selectedPackageForBooking = pkg;
    this.showBookAppointmentModal = true;
    this.appointmentSuccess = false;
    this.appointmentError = '';
    // Load locations when modal opens
    this.loadLocations();
  }

  onCloseModal(): void {
    this.showBookAppointmentModal = false;
    this.selectedPackageForBooking = null;
    this.appointmentSuccess = false;
    this.appointmentError = '';
  }

  onCloseSuccessModal(): void {
    this.showSuccessModal = false;
  }

  /**
   * Check if we're in admin/preview mode (has token in localStorage)
   */
  private isAdminMode(): boolean {
    try {
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');
      return !!(userData || token);
    } catch {
      return false;
    }
  }

  /**
   * Load and set active locations into component state, using LocationService in admin mode and handling loading/error states.
   * @example
   * oadLocations()
   * void
   * @returns {void} No return value; updates component state (locations, isLoadingLocations, locationsError) as a side effect.
   **/
  loadLocations(): void {
    if (this.locations.length > 0) {
      return; // Already loaded
    }

    if (this.isLoadingLocations) {
      return; // Already loading
    }

    this.isLoadingLocations = true;
    this.locationsError = '';

    // Check if in admin mode (has token) - use LocationService which handles auth
    if (this.isAdminMode()) {
      this.logger.debug('📍 Loading locations using LocationService (admin mode)');
      this.locationService.getLocations().subscribe({
        next: response => {
          this.isLoadingLocations = false;
          if (response && response.status && response.data && Array.isArray(response.data)) {
            // Filter only active locations
            this.locations = response.data.filter(
              (loc: any) => loc.status !== false && loc.active !== false
            );
            if (this.locations.length === 0) {
              this.locationsError = 'No active locations found.';
            }
          } else {
            this.locations = [];
            this.locationsError = 'No locations found.';
          }
        },
        error: error => {
          this.logger.error('Error loading locations via LocationService:', error);
          this.isLoadingLocations = false;
          this.locations = [];
          this.locationsError = 'No locations found. Please try again later.';
        },
      });
      return;
    }

    // Not in admin mode - for preview components, we require authentication
    this.logger.debug('📍 Not in admin mode - locations require authentication');
    this.isLoadingLocations = false;
    this.locations = [];
    this.locationsError = 'Please log in to load locations.';
  }

  /**
   * Check if we're in website preview mode (URL contains /preview)
   */
  private isWebsitePreviewMode(): boolean {
    try {
      return window.location.pathname.includes('/preview');
    } catch (error) {
      return false;
    }
  }

  /**
   * Schedules an appointment using provided form data: validates input, handles preview mode, constructs request and calls AppointmentService, and updates UI state and modals on success or error.
   * @example
   * nScheduleAppointment({ fullName: 'Jane Doe', email: 'jane@example.com', phoneNumber: '(555) 123-4567', serviceType: 'oil-change', licensePlate: 'ABC123', preferredDate: '2026-03-20', preferredTime: '09:00' })
   * void
   * @param {{any}} {{formData}} - Form data containing required appointment fields (fullName, email, phoneNumber, serviceType, licensePlate, preferredDate, preferredTime).
   * @returns {{void}} Void - Performs side effects (API call, UI state and modal updates) and does not return a value.
   **/
  onScheduleAppointment(formData: any): void {
    this.logger.debug('Appointment scheduled:', formData);

    // Check if we're in website preview mode - don't make API calls
    if (this.isWebsitePreviewMode()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'This feature is not available in website preview.',
        life: 5000,
      });
      this.onCloseModal();
      return;
    }

    // Validate required fields
    if (
      !formData.fullName ||
      !formData.email ||
      !formData.phoneNumber ||
      !formData.serviceType ||
      !formData.licensePlate ||
      !formData.preferredDate ||
      !formData.preferredTime
    ) {
      this.appointmentError = 'Please fill in all required fields.';
      return;
    }

    // Find the selected package
    const selectedPackage = this.packages.find(
      pkg => pkg.id === formData.serviceType || pkg.package_name === formData.serviceType
    );

    if (!selectedPackage || !selectedPackage.id) {
      this.appointmentError = 'Please select a valid service.';
      return;
    }

    // Get location_id - for now, use first available location or require location selection
    // TODO: Add location selection to the modal
    let locationId = '';
    if (this.locations.length > 0) {
      locationId = this.locations[0].id || this.locations[0].location_id || '';
    }

    if (!locationId) {
      this.appointmentError = 'No location available. Please try again later.';
      return;
    }

    // Format appointment time: combine date and time into ISO format
    const appointmentTime = this.formatAppointmentTime(
      formData.preferredDate,
      formData.preferredTime
    );
    if (!appointmentTime) {
      this.appointmentError = 'Invalid date or time selected.';
      return;
    }

    this.isSubmittingAppointment = true;
    this.appointmentError = '';
    this.appointmentSuccess = false;

    // Prepare request body for AppointmentService
    const requestBody = {
      full_name: formData.fullName,
      email: formData.email,
      contact: formData.phoneNumber.replace(/\D/g, ''), // Remove non-digits
      license_plate: formData.licensePlate.toUpperCase().trim(),
      package_id: selectedPackage.id,
      location_id: locationId,
      appointment_time: appointmentTime,
      source: 'website-builder',
    };

    // Use AppointmentService for authenticated API call
    this.appointmentService
      .createAppointment(requestBody)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Error creating appointment:', error);
          this.isSubmittingAppointment = false;
          let errorMessage = 'Failed to create appointment. Please try again.';

          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.appointmentError = errorMessage;
          return of({ status: false, message: errorMessage });
        })
      )
      .subscribe({
        next: response => {
          this.isSubmittingAppointment = false;

          if (response.status && response.status_code === 200) {
            this.appointmentSuccess = true;
            this.appointmentError = '';
            this.logger.debug('Appointment created successfully:', response.data);

            // Close appointment modal
            this.onCloseModal();

            // Prepare success modal data
            const appointmentData = response.data;
            const appointmentCode = appointmentData?.appointment_code || '';
            const appointmentTime = appointmentData?.appointment?.appointment_time;

            // Format appointment time for display
            let timeInfo = '';
            if (appointmentTime) {
              try {
                const date = new Date(appointmentTime);
                const formattedDate = date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });
                const formattedTime = date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });
                timeInfo = `${formattedDate} at ${formattedTime}`;
              } catch (e) {
                this.logger.error('Error formatting appointment time:', e);
              }
            }

            // Update success modal data
            this.successModalData = {
              title: 'Appointment Booked Successfully!',
              description: appointmentCode
                ? `Your appointment has been confirmed with code ${appointmentCode}. We look forward to serving you.`
                : 'Your appointment has been confirmed. We look forward to serving you.',
              buttonText: 'Okay',
              showTimeInfo: !!timeInfo,
              timeInfo: timeInfo,
            };

            // Show success modal
            this.showSuccessModal = true;
          } else {
            this.appointmentError = response.message || 'Failed to create appointment.';
          }
        },
      });
  }

  /**
   * Format a date string and a 12-hour time string (with AM/PM) into an ISO 8601 datetime string.
   * @example
   * formatAppointmentTime('2026-03-17', '9:00 AM')
   * '2026-03-17T09:00:00.000Z'
   * @param {{string}} {{dateString}} - Date string parseable by the JavaScript Date constructor (e.g., '2026-03-17').
   * @param {{string}} {{timeString}} - Time in 12-hour format with AM/PM (e.g., '9:00 AM' or '09:00 AM').
   * @returns {{string|null}} ISO 8601 datetime string in UTC (via toISOString()) or null if parsing fails.
   **/
  private formatAppointmentTime(dateString: string, timeString: string): string | null {
    try {
      // Parse date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }

      // Parse time (format: "9:00 AM" or "09:00 AM")
      const timeMatch = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!timeMatch) {
        return null;
      }

      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const period = timeMatch[3].toUpperCase();

      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      // Set time on date
      date.setHours(hours, minutes, 0, 0);

      // Return ISO string
      return date.toISOString();
    } catch (error) {
      this.logger.error('Error formatting appointment time:', error);
      return null;
    }
  }

  getServiceOptions(): { value: string; label: string }[] {
    return this.packages.map(pkg => ({
      value: pkg.id || pkg.package_name,
      label: pkg.package_name,
    }));
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
